'use client';

import clsx from 'clsx';
import type { ReactNode, ForwardedRef } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps } from '../types.js';

export interface UNSTABLE_VisuallyHiddenProps extends CommonProps {
  /** The content that should be hidden visually. */
  children: ReactNode;
  /**
   * Whether to show the content when it's focused. This can be used to implement a [skip link](https://webaim.org/techniques/skipnav/).
   * @default false
   */
  showWhenFocused?: boolean;
}

function UNSTABLE_VisuallyHidden(
  { children, UNSAFE_className, UNSAFE_style, showWhenFocused, ...props }: UNSTABLE_VisuallyHiddenProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={clsx('cim-visually-hidden', showWhenFocused && 'cim-visually-hidden-focusable', UNSAFE_className)}
      style={UNSAFE_style}
    >
      {children}
    </div>
  );
}

/**
 * Renders a container that hides its children visually, while keeping content accessible to screen readers.
 */
const _VisuallyHidden = withStyleProps(forwardRef(UNSTABLE_VisuallyHidden), 'VisuallyHidden');

export { _VisuallyHidden as UNSTABLE_VisuallyHidden };
